import Button from '2-core-elements/Buttons/Button';
import Image, { type ImageProps } from '2-core-elements/Image/Image';
import LinkCTA from '2-core-elements/LinkCTA/LinkCTA';
import Badge, { type BadgeProps } from '3-components/Badge/Badge';
import BadgeGroup from '3-components/BadgeGroup/BadgeGroup';
import { type FC } from 'react';

export type ImageCardProps = {
    href?: string;
    padded?: boolean;
    size?: 'sm' | 'md' | 'lg';
    img?: Pick<ImageProps, 'imgSrc' | 'imgAlt' | 'imgSrcSet'>;
    title: string;
    subtitle?: string;
    children?: React.ReactNode;
    ctaText?: string;
    ctaSize?: 'sm' | 'md' | 'lg';
    asDiv?: boolean;
    asLink?: boolean;
    tags?: BadgeProps[];
};

const ImageCard: FC<ImageCardProps> = ({
    href,
    padded = true,
    size = 'md',
    img,
    title,
    children,
    ctaText,
    ctaSize = 'md',
    asDiv = false,
    asLink = false,
    tags = [],
    subtitle,
}) => {
    const cardClasses = `image-card ${!padded ? 'card-no-padding' : ''} card-${size}`;

    const content = (
        <>
            {img && (
                <div className="card-img">
                    <Image
                        imgSrc={img.imgSrc}
                        imgAlt={img.imgAlt}
                        imgSrcSet={img.imgSrcSet}
                        imgWidth={450}
                        imgHeight={350}
                        aspectRatio="16/9"
                    />
                </div>
            )}
            {!!tags?.length && (
                <BadgeGroup className="card-tags">
                    {tags.map((tag, i) => {
                        const key = `${tag.label}-${i}`;
                        return <Badge key={key} label={tag.label} skin={tag.skin} prefixIcon={tag.prefixIcon} variant={tag.variant} size="md" />;
                    })}
                </BadgeGroup>
            )}
            <div className="card-content">
                <h3 className="card-title">{title}</h3>
                {!!subtitle && <p className="card-subtitle">{subtitle}</p>}
                {children && (
                    <div className="card-description">
                        {children}
                    </div>
                )}
                {ctaText && (
                    <div className="card-cta">
                        {asLink ? (
                            <LinkCTA
                                size={ctaSize}
                                className="image-card-link-cta"
                                href={asDiv ? href : ''}
                            >
                                {ctaText}
                            </LinkCTA>
                        ) : (
                            <Button
                                size={ctaSize}
                                asDiv={true}
                                className="image-card-button"
                                href={asDiv ? href : ''}
                            >
                                {ctaText}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </>
    );

    if (asDiv) {
        return (
            <div className={cardClasses}>
                {content}
            </div>
        );
    }

    return (
        <a href={href} className={cardClasses}>
            {content}
        </a>
    );
};

export default ImageCard;
